"use client";

import { c as _c } from "react/compiler-runtime";
import { type UseQueryOptions, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "sonner";
import { saveDraftProfile } from "@/lib/bap/backup";
import type { BAPProfile } from "@/lib/bap/core";
import { fetchBAPProfileForQuery } from "@/lib/bap/core";
export type { BAPProfile } from "@/lib/bap/core";

/**
 * Canonical query key namespace for BAP data.
 *
 * Every read AND mutation in the app must use these helpers — duplicate key
 * conventions across modules silently break TanStack Query invalidation
 * (saving a profile in one component fails to refresh another reading the
 * same data) and forces us into manual event-bus glue. One namespace, one
 * source of truth.
 */
export const bapKeys = {
  all: ["bap"] as const,
  profile: (bapId: string) => [...bapKeys.all, "profile", bapId] as const,
  identities: () => [...bapKeys.all, "identities"] as const
};

/** Fetch a single BAP profile by id. */
export function useBapProfile(bapId, opts) {
  const $ = _c(9);
  let t0;
  if ($[0] !== bapId) {
    t0 = bapKeys.profile(bapId ?? "");
    $[0] = bapId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== bapId) {
    t1 = () => {
      if (!bapId) {
        return Promise.resolve(null);
      }
      return fetchBAPProfileForQuery(bapId);
    };
    $[2] = bapId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = !!bapId;
  let t3;
  if ($[4] !== opts || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 30000,
      retry: 1,
      ...opts
    };
    $[4] = opts;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

/** Fetch profiles for many BAP ids in parallel, sharing the same cache. */
export function useBapProfiles(bapIds) {
  const $ = _c(4);
  let t0;
  if ($[0] !== bapIds) {
    t0 = bapIds.map(_temp);
    $[0] = bapIds;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      queries: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useQueries(t1);
}

/** Imperatively invalidate one profile's cache (forces refetch).
 *
 * Wrapped in `useCallback` so the returned function has a stable identity
 * across renders. Without this, any `useEffect` that depends on this
 * function would re-fire on every render and risk an infinite loop.
 */
function _temp(bapId) {
  return {
    queryKey: bapKeys.profile(bapId),
    queryFn: () => fetchBAPProfileForQuery(bapId),
    staleTime: 0,
    retry: 1
  };
}
export function useInvalidateBapProfile() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = bapId => {
      queryClient.invalidateQueries({
        queryKey: bapKeys.profile(bapId)
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}

/**
 * Save (upsert) a draft BAP profile. On success, invalidates the matching
 * profile key — every component reading via useBapProfile / useBapProfiles
 * automatically refetches. No events. No manual hand-off.
 */
export function useSaveDraftProfile() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: (_, t1) => {
        const {
          bapId: bapId_0
        } = t1;
        queryClient.invalidateQueries({
          queryKey: bapKeys.profile(bapId_0)
        });
        queryClient.invalidateQueries({
          queryKey: bapKeys.identities()
        });
        toast.success("Profile saved");
      },
      onError: _temp3
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}

/** Delete a draft BAP profile (e.g. after publishing on-chain). */
function _temp3(error) {
  console.error("Error saving draft profile:", error);
  toast.error(error instanceof Error ? error.message : "Failed to save profile");
}
async function _temp2(t0) {
  const {
    bapId,
    profile
  } = t0;
  await saveDraftProfile(bapId, profile);
  return {
    success: true
  };
}
export function useDeleteDraftProfile() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp5,
      onSuccess: (_, bapId_0) => {
        queryClient.invalidateQueries({
          queryKey: bapKeys.profile(bapId_0)
        });
        queryClient.invalidateQueries({
          queryKey: bapKeys.identities()
        });
        toast.success("Draft discarded");
      },
      onError: _temp6
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp6(error) {
  console.error("Error deleting draft profile:", error);
  toast.error(error instanceof Error ? error.message : "Failed to discard draft");
}
async function _temp5(bapId) {
  const response = await fetch(`/api/bap/profile?bapId=${bapId}`, {
    method: "DELETE",
    credentials: "include"
  });
  if (!response.ok) {
    const errorData = await response.json().catch(_temp4);
    throw new Error(errorData.error || "Failed to delete draft profile");
  }
  return response.json();
}
function _temp4() {
  return {};
}